import { useQuery } from '@tanstack/react-query';
import Drawer from '@/components/ui/Drawer';
import Badge from '@/components/ui/Badge';
import SportBadge from '@/components/ui/SportBadge';
import GuidanceText from '@/components/ui/GuidanceText';
import { getWorkout, WorkoutInterval } from '@/api/workoutsService';
import { queryKeys } from '@/lib/queryKeys';

const SECTION_LABELS: Record<string, string> = {
  WARMUP: 'Échauffement',
  MAIN: 'Corps de séance',
  COOLDOWN: 'Retour au calme',
};

const SECTION_ORDER = ['WARMUP', 'MAIN', 'COOLDOWN'] as const;

const expandRows = (interval: WorkoutInterval): WorkoutInterval[] =>
  interval.children.length > 0 && interval.repeatCount <= 1 ? interval.children : [interval];

// PPG séance PDFs are hosted publicly and linked at display time from the séance number
// (e.g. "Séance 7" → /ppg/7.pdf). The URL is never stored (PRD §5).
const ppgPdfUrl = (sportIdentifier: string, name: string): string | null => {
  if (sportIdentifier !== 'PPG') return null;
  const match = name.match(/(\d+)/);
  return match ? `https://www.opentri.fr/ppg/${match[1]}.pdf` : null;
};

const formatDuration = (sec: number | null): string | null => {
  if (sec === null) return null;
  if (sec % 60 === 0) return `${sec / 60} min`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m} min ${s}s` : `${s}s`;
};

const formatMeasure = (node: WorkoutInterval): string | null => {
  if (node.isMax) return 'Max';
  if (node.reps !== null) return `${node.reps} rép.`;
  if (node.durationSec !== null) return formatDuration(node.durationSec);
  if (node.distanceM !== null) return `${node.distanceM} m`;
  return null;
};

const formatEffort = (node: WorkoutInterval): string | null => {
  if (node.allOut) return 'All-out';
  if (node.intensityPctIcMin !== null || node.intensityPctIcMax !== null) {
    if (node.intensityPctIcMin !== null && node.intensityPctIcMax !== null && node.intensityPctIcMin !== node.intensityPctIcMax) {
      return `${node.intensityPctIcMin}–${node.intensityPctIcMax} %`;
    }
    return `${node.intensityPctIcMin ?? node.intensityPctIcMax} %`;
  }
  if (node.zoneLabel) return node.zoneLabel;
  if (node.rpe !== null) return `RPE ${node.rpe}`;
  return null;
};

const IntervalNode = ({ node, depth }: { node: WorkoutInterval; depth: number }) => {
  const isGroup = node.children.length > 0;
  const measure = formatMeasure(node);
  const effort = formatEffort(node);

  return (
    <li className="text-sm">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-700 dark:text-gray-200">
        {node.repeatCount > 1 && <span className="font-semibold text-brand-500">{node.repeatCount} ×</span>}
        {isGroup ? (
          <span className="font-medium text-gray-500 dark:text-gray-400">Bloc</span>
        ) : (
          <>
            {measure && <span className="font-medium">{measure}</span>}
            {effort && <span className="text-gray-500 dark:text-gray-400">@ {effort}</span>}
            {node.cadence !== null && <span className="text-gray-500 dark:text-gray-400">· {node.cadence} rpm</span>}
            {node.unilateral && <span className="text-gray-500 dark:text-gray-400">· par membre</span>}
          </>
        )}
        {node.note?.cue && <span className="italic text-gray-400">— {node.note.cue}</span>}
        {node.note?.equipment.map((item) => (
          <span
            key={item}
            className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300"
          >
            {item}
          </span>
        ))}
      </div>
      {isGroup && (
        <ul className="mt-1 space-y-1 border-l border-gray-200 pl-4 dark:border-gray-700" style={{ marginLeft: depth * 2 }}>
          {node.children.map((child) => (
            <IntervalNode key={child.id} node={child} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
};

interface WorkoutPreviewPanelProps {
  workoutId: string | null;
  /** Renders the workout scaled for this plan format (simulator); authored when omitted. */
  format?: string | null;
  onClose: () => void;
}

const WorkoutPreviewPanel = ({ workoutId, format, onClose }: WorkoutPreviewPanelProps) => {
  const { data, isLoading } = useQuery({
    queryKey: workoutId
      ? format
        ? [...queryKeys.pv2Workout(workoutId), format]
        : queryKeys.pv2Workout(workoutId)
      : ['admin', 'pv2', 'workout', 'none'],
    queryFn: () => getWorkout(workoutId as string, format),
    enabled: Boolean(workoutId),
  });

  const renderedSections = data?.rendered.sections ?? [];
  const renderedDuration = data?.rendered.durationSec ?? null;
  const renderedDistance = data?.rendered.distanceM ?? null;

  return (
    <Drawer open={Boolean(workoutId)} onClose={onClose} title={data?.name ?? 'Aperçu de la séance'}>
      {isLoading && <p className="text-sm text-gray-500">Chargement…</p>}

      {data && (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-2">
            {data.sportIdentifier && <SportBadge identifier={data.sportIdentifier} label={data.sportLabel} />}
            <Badge color="gray">{data.themeLabel}</Badge>
            {renderedDistance !== null && <Badge color="gray">{renderedDistance} m</Badge>}
            {renderedDuration !== null && <Badge color="gray">{formatDuration(renderedDuration)}</Badge>}
            {data.renderedFormat && <Badge color="blue">Adapté au format {data.renderedFormat}</Badge>}
            {data.isDraft && <Badge color="yellow">Brouillon</Badge>}
            {!data.isStructured && <Badge color="gray">Non structurée</Badge>}
          </div>

          {data.note && <p className="text-sm text-gray-600 dark:text-gray-300">{data.note}</p>}

          {ppgPdfUrl(data.sportIdentifier, data.name) && (
            <a
              href={ppgPdfUrl(data.sportIdentifier, data.name) as string}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm font-medium text-brand-500 hover:underline"
            >
              Voir la séance détaillée (PDF) ↗
            </a>
          )}

          <section>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-400">Structure</h3>
            {renderedSections.length === 0 && !data.contentText && (
              <p className="text-sm text-gray-500">Aucun intervalle.</p>
            )}
            {data.contentText && renderedSections.length === 0 && (
              <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-200">{data.contentText}</p>
            )}
            {SECTION_ORDER.map((section) => {
              const parts = renderedSections.filter((s) => s.section === section);
              if (parts.length === 0) return null;
              const fromTheme = parts.some((s) => s.source === 'theme');
              const intervals = parts.flatMap((s) => s.intervals);
              if (intervals.length === 0) return null;
              return (
                <div key={section} className="mb-4">
                  <p className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                    {SECTION_LABELS[section]}
                    {fromTheme && (
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium normal-case text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                        d'après le thème
                      </span>
                    )}
                  </p>
                  <ul className="space-y-1">
                    {intervals.flatMap(expandRows).map((node) => (
                      <IntervalNode key={node.id} node={node} depth={0} />
                    ))}
                  </ul>
                </div>
              );
            })}
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-400">Mot du coach</h3>
            <GuidanceText text={data.coachNote} />
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-400">Nutrition</h3>
            <GuidanceText text={data.nutrition} />
          </section>
        </div>
      )}
    </Drawer>
  );
};

export default WorkoutPreviewPanel;
